'use client'

import { Suspense, useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { MenuIcon, ChevronDownIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { MARKETING_ENGINES } from '@/lib/marketing/engines'
import { Logo } from '@/components/marketing/logo'
import { Button } from '@/components/ui/button'
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ThemeSwitcher } from '@/components/shared/theme-switcher'

// --- NavDropdown ---

interface NavDropdownItem {
  label: string
  href: string
}

interface NavDropdownColumn {
  heading: string
  items: NavDropdownItem[]
}

interface NavDropdownProps {
  label: string
  columns: NavDropdownColumn[]
}

function NavDropdown({ label, columns }: NavDropdownProps) {
  const [open, setOpen] = useState(false)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current)
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
  }, [])

  const handleMouseEnter = useCallback(() => {
    clearTimers()
    openTimerRef.current = setTimeout(() => setOpen(true), 50)
  }, [clearTimers])

  const handleMouseLeave = useCallback(() => {
    clearTimers()
    closeTimerRef.current = setTimeout(() => setOpen(false), 150)
  }, [clearTimers])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false)
  }, [])

  const handleTriggerClick = useCallback(() => {
    clearTimers()
    setOpen((prev) => !prev)
  }, [clearTimers])

  // Close on outside click
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleTriggerClick}
        className="flex items-center gap-1 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
      >
        {label}
        <ChevronDownIcon
          className={cn('size-3.5 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute left-0 top-full mt-2 min-w-[360px] max-w-[420px]',
            'rounded-lg border border-border bg-background shadow-sm',
            'animate-in fade-in-0 slide-in-from-top-2 duration-200',
            'z-50 p-4'
          )}
        >
          <div className={cn('grid gap-6', columns.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
            {columns.map((col) => (
              <div key={col.heading}>
                <p className="type-overline mb-2 text-muted-foreground">{col.heading}</p>
                <ul className="space-y-0">
                  {col.items.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        role="menuitem"
                        className="block py-1.5 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                        onClick={() => setOpen(false)}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Nav config ---

const SOLUTIONS_COLUMNS: NavDropdownColumn[] = [
  {
    heading: 'By Role',
    items: [
      { label: 'Marketing Leaders', href: '/for/marketing-leaders' },
      { label: 'SEO Teams', href: '/for/seo-teams' },
      { label: 'Brand Managers', href: '/for/brand-managers' },
      { label: 'Agencies', href: '/for/agencies' },
      { label: 'Founders', href: '/for/founders' },
    ],
  },
  {
    heading: 'By Industry',
    items: [
      { label: 'SaaS', href: '/for/saas' },
      { label: 'E-commerce', href: '/for/ecommerce' },
      { label: 'See all', href: '/for' },
    ],
  },
]

const RESOURCES_COLUMNS: NavDropdownColumn[] = [
  {
    heading: 'Learn',
    items: [
      { label: 'Blog', href: '/blog' },
      { label: 'Guides', href: '/guides' },
      { label: 'Glossary', href: '/glossary' },
    ],
  },
  {
    heading: 'By Engine',
    items: MARKETING_ENGINES.map((e) => ({
      label: `${e.displayName} Visibility`,
      href: e.visibilityPageHref,
    })),
  },
]

// --- Free scan link ---

/** The one free tool: its landing page. The desktop nav and the mobile menu both link here. */
const FREE_SCAN_NAV_LABEL = 'Free AI visibility scan'
const FREE_SCAN_NAV_HREF = '/tools/ai-visibility-scan'

// --- MobileSection ---

interface MobileSectionProps {
  heading: string
  children: React.ReactNode
}

function MobileSection({ heading, children }: MobileSectionProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-muted"
        aria-expanded={expanded}
      >
        {heading}
        <ChevronDownIcon
          className={cn(
            'size-4 text-muted-foreground transition-transform duration-300',
            expanded && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          expanded ? '[grid-template-rows:1fr]' : '[grid-template-rows:0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="pb-1 pl-3 pt-1">{children}</div>
        </div>
      </div>
    </div>
  )
}

// --- MobileNavLink ---

interface MobileNavLinkProps {
  href: string
  /** Closes the menu: the header outlives the navigation, so the sheet would stay open. */
  onNavigate: () => void
  /** A row inside a MobileSection (smaller, muted) rather than a top-level row. */
  nested?: boolean
  /** A top-level row in the muted tone (the log-in link). */
  muted?: boolean
  children: React.ReactNode
}

/**
 * A navigation link in the mobile menu. A plain `<Link>`, not `SheetClose render={<Link />}`: a
 * Base UI close button either demands a native `<button>` (a console error on every open) or, with
 * `nativeButton={false}`, stamps `role="button"` on the anchor, and a destination is a link.
 */
function MobileNavLink({ href, onNavigate, nested, muted, children }: MobileNavLinkProps) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={cn(
        'rounded-lg px-3 transition-colors duration-200 hover:bg-muted',
        nested
          ? 'block py-1.5 text-sm text-muted-foreground hover:text-foreground'
          : muted
            ? 'py-2 text-sm text-muted-foreground hover:text-foreground'
            : 'py-2 text-sm font-medium text-foreground'
      )}
    >
      {children}
    </Link>
  )
}

// --- MobileStickyBar ---

function MobileStickyBar({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden={!visible}
      inert={!visible || undefined}
      className={cn(
        'fixed bottom-0 inset-x-0 z-40 md:hidden',
        'border-t border-border bg-background/95 backdrop-blur-lg px-4 py-3',
        'transition-transform duration-300 ease-out',
        visible ? 'translate-y-0' : 'translate-y-full'
      )}
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="flex gap-2">
        <Button
          render={<Link href="/auth/login?mode=signup&ref=mobile-sticky" />}
          nativeButton={false}
          className="flex-1"
          size="lg"
        >
          Start free trial
        </Button>
        <Button
          render={<Link href="/tools/ai-visibility-scan?ref=mobile-sticky" />}
          nativeButton={false}
          variant="outline"
          size="lg"
        >
          Free scan
        </Button>
      </div>
    </div>
  )
}

/**
 * The header persists across marketing routes, so the mobile sheet must close on any navigation: a tap
 * on the sheet's logo link, or browser back/forward, never reaches `MobileNavLink`'s onClick. Reading
 * the pathname is runtime data on a dynamic segment, so it lives in this leaf behind its own
 * `<Suspense>` — read in `MarketingHeader` itself, it made the whole marketing layout block.
 */
function CloseMenuOnNavigate({ onNavigate }: { onNavigate: () => void }) {
  const pathname = usePathname()
  const seen = useRef(pathname)
  useEffect(() => {
    if (seen.current === pathname) return
    seen.current = pathname
    onNavigate()
  }, [pathname, onNavigate])
  return null
}

// --- MarketingHeader ---

export function MarketingHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [compressed, setCompressed] = useState(false)
  const [showMobileBar, setShowMobileBar] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  // True when the user is near the bottom of the page (FinalCtaSection visible) — hide sticky bar to avoid overlap
  const [finalCtaVisible, setFinalCtaVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY
      setScrolled(y > 10)
      setCompressed(y > 80)
      // Show mobile sticky CTA after scrolling past typical hero CTA (~300px)
      setShowMobileBar(y > 300)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Hide the sticky bar when the user is near the bottom of the page
    // (within ~300px of the FinalCtaSection / footer area), avoiding overlap
    const checkNearBottom = () => {
      const distanceFromBottom =
        document.documentElement.scrollHeight - window.scrollY - window.innerHeight
      setFinalCtaVisible(distanceFromBottom < 300)
    }
    window.addEventListener('scroll', checkNearBottom, { passive: true })
    checkNearBottom()
    return () => window.removeEventListener('scroll', checkNearBottom)
  }, [])

  return (
    <>
      <header
        className={cn(
          // Height transitions from 64px → 52px at scrollY > 80, smooth 200ms
          'sticky inset-x-0 top-0 z-50 transition-[height,background-color,border-color,backdrop-filter] duration-200',
          compressed ? 'h-[52px]' : 'h-16',
          scrolled
            ? 'border-b border-border bg-background/85 backdrop-blur-xl saturate-150'
            : 'bg-transparent'
        )}
      >
        <div className="wrap-wide flex h-full items-center justify-between px-6">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-5 md:flex">
            <NavDropdown label="Solutions" columns={SOLUTIONS_COLUMNS} />

            <NavDropdown label="Resources" columns={RESOURCES_COLUMNS} />

            <Link
              href={FREE_SCAN_NAV_HREF}
              className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              {FREE_SCAN_NAV_LABEL}
            </Link>
            <Link
              href="/compare"
              className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              Compare
            </Link>
            <Link
              href="/pricing"
              className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              Pricing
            </Link>
          </nav>

          {/* Desktop right side: ThemeSwitcher between nav links and auth CTAs */}
          <div className="hidden items-center gap-3 md:flex">
            <ThemeSwitcher />
            <Link
              href="/auth/login"
              className="text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              Log in
            </Link>
            <Button
              render={<Link href="/auth/login?mode=signup&ref=header" />}
              nativeButton={false}
              size="sm"
            >
              Start free trial
            </Button>
          </div>

          {/* Mobile hamburger */}
          <Suspense fallback={null}>
            <CloseMenuOnNavigate onNavigate={closeMenu} />
          </Suspense>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger render={<Button variant="ghost" size="icon-sm" className="md:hidden" />}>
              <MenuIcon />
              <span className="sr-only">Open menu</span>
            </SheetTrigger>

            <SheetContent side="right" className="flex flex-col overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="sr-only">Navigation</SheetTitle>
              </SheetHeader>

              <div className="mb-4 px-2">
                <Logo />
              </div>

              <nav className="flex flex-col gap-1 px-2">
                {/* Solutions */}
                <MobileSection heading="Solutions">
                  <p className="type-overline mb-1 px-3 text-muted-foreground">By Role</p>
                  {SOLUTIONS_COLUMNS[0]?.items.map((item) => (
                    <MobileNavLink key={item.href} href={item.href} nested onNavigate={closeMenu}>
                      {item.label}
                    </MobileNavLink>
                  ))}
                  <p className="type-overline mb-1 mt-3 px-3 text-muted-foreground">By Industry</p>
                  {SOLUTIONS_COLUMNS[1]?.items.map((item) => (
                    <MobileNavLink key={item.href} href={item.href} nested onNavigate={closeMenu}>
                      {item.label}
                    </MobileNavLink>
                  ))}
                </MobileSection>

                {/* Resources */}
                <MobileSection heading="Resources">
                  <p className="type-overline mb-1 px-3 text-muted-foreground">Learn</p>
                  {RESOURCES_COLUMNS[0]?.items.map((item) => (
                    <MobileNavLink key={item.href} href={item.href} nested onNavigate={closeMenu}>
                      {item.label}
                    </MobileNavLink>
                  ))}
                  <p className="type-overline mb-1 mt-3 px-3 text-muted-foreground">By Engine</p>
                  {RESOURCES_COLUMNS[1]?.items.map((item) => (
                    <MobileNavLink key={item.href} href={item.href} nested onNavigate={closeMenu}>
                      {item.label}
                    </MobileNavLink>
                  ))}
                </MobileSection>

                {/* Free AI visibility scan — single link */}
                <MobileNavLink href={FREE_SCAN_NAV_HREF} onNavigate={closeMenu}>
                  {FREE_SCAN_NAV_LABEL}
                </MobileNavLink>

                {/* Compare — single link */}
                <MobileNavLink href="/compare" onNavigate={closeMenu}>
                  Compare
                </MobileNavLink>

                {/* Pricing — single link */}
                <MobileNavLink href="/pricing" onNavigate={closeMenu}>
                  Pricing
                </MobileNavLink>
              </nav>

              {/* Appearance row — between nav and auth section */}
              <div className="mx-2 mt-4 border-t border-border" />
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">Appearance</span>
                <ThemeSwitcher />
              </div>

              <div className="mx-2 border-t border-border" />

              <div className="flex flex-col gap-3 px-2 py-4">
                <MobileNavLink href="/auth/login" muted onNavigate={closeMenu}>
                  Log in
                </MobileNavLink>
                <Button
                  render={<Link href="/auth/login?mode=signup&ref=header" />}
                  nativeButton={false}
                  className="w-full"
                >
                  Start free trial
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Mobile-only sticky bottom CTA — visible after scroll, hidden on desktop, hides when near page bottom to avoid FinalCta overlap */}
      <MobileStickyBar visible={showMobileBar && !finalCtaVisible} />
    </>
  )
}
